import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Paper,
  Grid,
  Card,
  Switch,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import EmptyData from "../../components/EmptyData";
import apiClient from "../../Utils/apiClient";
import socketService from "../../socket/newsocketService";
import LongMenu from "Utils/action";
import File from "Utils/file";
import ReportGenerate from "Utils/reportGenerate";
import BatteryStatusChip from "layouts/Escort/BatteryStatusChip";
import UserFBK from "layouts/Escort/userFBK";
import WellBeingConnect from "layouts/Escort/WellBeingConnect";
import WellBeingMovement from "layouts/Escort/WellBeingMovement";

const getBatteryPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric <= 1 && numeric > 0 ? numeric * 100 : numeric));
};

const VirtualTravelGuard = () => {
  const getUserState = useSelector((state) => state?.data?.getUserState);
  console.log("[VirtualTravelGuard] component render", {
    getUserState,
  });

  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [checkSuperAdmin, setCheckSuperAdmin] = useState(null);
  const [adminState, setAdminState] = useState(null);

  const [elapsedTimes, setElapsedTimes] = useState({});

  const navigate = useNavigate();

  const normalizeLogRecord = useCallback((record) => {
    const payload = record?.payload || {};
    const userId = record?.userId || payload?.userId || "";
    const socketId = record?.socketId || payload?.socketId || null;
    const userImage = payload?.userImage || record?.userImage || "";
    const userName =
      payload?.userName || record?.userName || payload?.Name || record?.Name || "Unknown User";
    const state = record?.state || payload?.state || record?.userState || payload?.userState || "";
    const rawBattery =
      payload?.BatteryIndicator ??
      payload?.batteryIndicator ??
      payload?.batteryLevel ??
      record?.BatteryIndicator ??
      record?.batteryIndicator ??
      record?.batteryLevel ??
      0;
    const batteryIndicator = getBatteryPercent(rawBattery);
    const tracking = payload?.tracking ?? false;
    const isCharging = Boolean(payload?.isCharging ?? record?.isCharging);
    const wellbeingResponse =
      payload?.Wellbeing_Response ||
      payload?.wellbeingResponse ||
      record?.Wellbeing_Response ||
      record?.wellbeingResponse ||
      "Not Triggered";
    const userFBK =
      payload?.userFBK ||
      payload?.userFeedback ||
      record?.userFBK ||
      record?.userFeedback ||
      "No Feedback";

    return {
      ...payload,
      ...record,
      userId,
      wellbeingIdd:
        record?._id || payload?.key || payload?.id || record?.id || `${userId || "unknown"}-${record?.createdAt || Date.now()}`,
      Wellbeing_Response: wellbeingResponse,
      wellbeingResponse: wellbeingResponse,
      userFBK: userFBK,
      BatteryIndicator: batteryIndicator,
      batteryLevel: batteryIndicator,
      isCharging,
      userData: {
        userId,
        socketId,
        userImage,
        userName,
        state,
        userFBK,
        BatteryIndicator: batteryIndicator,
        Wellbeing_Response: wellbeingResponse,
        wellbeingResponse: wellbeingResponse,
        tracking,
        is_moving: payload?.is_moving ?? payload?.tracking ?? false,
        isCharging,
      },
      currentLocTxt: payload?.currentLocTxt || payload?.currentLoc || "---",
      destLocTxt: payload?.destLocTxt || payload?.destLoc || "---",
      VehicleNumber: payload?.VehicleNumber || payload?.vehicleNumber || "N/A",
      TripDate: payload?.TripDate || payload?.Date || "N/A",
      ArrivalDate: payload?.ArrivalDate || "N/A",
      ArrivalTime: payload?.ArrivalTime || "N/A",
      PartenerType:
        payload?.PartenerType || payload?.PartnerRelation || payload?.PartnerType || "N/A",
      Name: payload?.Name || payload?.PartnerName || "N/A",
      PhoneNumber: payload?.PhoneNumber || payload?.PartnerMobileNo || "N/A",
      tracking,
      startTime: payload?.startTime || payload?.StartTime || 0,
    };
  }, []);

  const getWellBeingServicesData = useCallback(async () => {
    setLoading(true);
    console.log("[VirtualTravelGuard] loading backend VTG data...");

    const adminInfo = JSON.parse(localStorage.getItem("user"));
    const adminState = adminInfo?.state;
    const isSuperAdmin = adminInfo?.superAdmin;
    const selectedState =
      getUserState === "" || getUserState === "All State" ? "" : getUserState;
    const stateFilter = isSuperAdmin ? selectedState : adminState;

    console.log("[VirtualTravelGuard] admin info", {
      adminState,
      isSuperAdmin,
      selectedState: getUserState,
    });
    console.log("[VirtualTravelGuard] data source", {
      type: "api + socket",
      api: "/virtual-travel-guard",
      socketEvents: ["virtualtravelguard", "wellbeingescort"],
      stateFilter: stateFilter || "ALL",
    });

    setCheckSuperAdmin(isSuperAdmin);
    setAdminState(adminState);

    try {
      const params = {
        page: 1,
        limit: 500,
      };

      if (stateFilter) {
        params.state = stateFilter;
      }

      const response = await apiClient.get("/virtual-travel-guard", { params });
      const records = Array.isArray(response?.data) ? response.data : [];

      console.log("[VirtualTravelGuard] api response", {
        count: records.length,
        pagination: response?.pagination || null,
      });

      const rows = await Promise.all(
        records.map((record) => normalizeLogRecord(record)),
      );

      const filteredRows = rows.filter(record => {
        const recordState = record?.state || record?.userData?.state || "";
        const passesStateFilter =
          !stateFilter || recordState === stateFilter || getUserState === "All State";

        return passesStateFilter;
      });

      filteredRows.sort((a, b) => {
        const left = b?.createdAt || b?.updatedAt || 0;
        const right = a?.createdAt || a?.updatedAt || 0;
        return left - right;
      });

      const uniqueRows = [];
      const seenKeys = new Set();

      filteredRows.forEach((record, index) => {
        const dedupeKey =
          String(
            record?._id ||
            record?.wellbeingIdd ||
            record?.payload?.key ||
            record?.payload?._id ||
            record?.key ||
            `${record?.userId || "user"}-${record?.createdAt || record?.TripDate || index}`
          ).trim();

        if (seenKeys.has(dedupeKey)) {
          return;
        }

        seenKeys.add(dedupeKey);
        uniqueRows.push(record);
      });

      console.log("[VirtualTravelGuard] api filtered result", {
        finalRows: uniqueRows.length,
        sample: uniqueRows[0] || null,
      });

      setAllData(uniqueRows);
    } catch (error) {
      console.log("[VirtualTravelGuard] api fetch error", error);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  }, [getUserState, normalizeLogRecord]);

  const handleBackendRefresh = useCallback(
    payload => {
      console.log("[VirtualTravelGuard] socket event received", payload);
      getWellBeingServicesData();
    },
    [getWellBeingServicesData],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);

      setElapsedTimes(() => {
        const updated = {};

        allData.forEach((user) => {
          if (user?.startTime) {
            const elapsed = now - user.startTime;
            updated[user.userId] = elapsed > 0 ? formatTime(elapsed) : "00:00:00";
          }
        });

        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [allData]);

  useEffect(() => {
    console.log("[VirtualTravelGuard] socket listeners registering");
    socketService.on("virtualtravelguard", handleBackendRefresh);
    socketService.on("wellbeingescort", handleBackendRefresh);

    return () => {
      socketService.off("virtualtravelguard", handleBackendRefresh);
      socketService.off("wellbeingescort", handleBackendRefresh);
    };
  }, [handleBackendRefresh]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, "0");
    const mins = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const secs = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  const handleToggle = async (user, index) => {
    //console.warn(user, index, allData[index].tracking);
    console.log('[VirtualTravelGuard] toggle tracking clicked', {
      userId: user?.userId,
      recordId: user?.wellbeingIdd,
      previousTracking: allData[index]?.tracking,
    });

    setAllData((prevData) => {
      const updatedData = [...prevData];
      updatedData[index] = { ...updatedData[index], tracking: !updatedData[index].tracking };
      return updatedData;
    });

    const trackingPayload = {
      console: "Virtual Travel Guard",
      socketId: user?.socketId || user?.userData?.socketId,
      userId: user?.userId,
      tracking: !allData[index].tracking,
    };

    console.log('[VirtualTravelGuard] emitting socket event trackingData', trackingPayload);

    socketService.emit("trackingData", {
      ...trackingPayload,
    });

    toast.info(`Tracking has been updated`, {
      autoClose: 3000,
      position: toast.POSITION.TOP_CENTER,
    });
  };

  useEffect(() => {
    const user = localStorage.getItem("user");
    console.log('[VirtualTravelGuard] page mounted', {
      hasLocalUser: Boolean(user),
      selectedState: getUserState,
    });
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }

    getWellBeingServicesData();
  }, [getUserState, getWellBeingServicesData, navigate]);

  return (
    <DashboardLayout>
      <MDBox pt={12} pb={3}>
        <Grid container spacing={6} style={{ position: "relative", minHeight: "60vh" }}>
          {loading ? (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Loading...
            </div>
          ) : (
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{
                    textAlign: "center",
                  }}
                >
                  <ToastContainer />
                  <MDTypography
                    variant="h6"
                    color="white"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1.5rem",
                    }}
                  >
                    {checkSuperAdmin
                      ? getUserState === ""
                        ? "ALL STATE "
                        : `${getUserState?.toUpperCase()} STATE `
                      : `${adminState?.toUpperCase()} STATE `}
                    VIRTUAL TRAVEL GUARD
                  </MDTypography>
                </MDBox>
                {allData.length > 0 ? (
                  <MDBox>
                    <TableContainer component={Paper}>
                      <Table
                        aria-label="user table"
                        sx={{ "& td, & th": { fontSize: "13px", padding: "8px" } }}
                      >
                        <TableHead>
                          <TableBody>
                            <TableCell>User Name</TableCell>
                            <TableCell align="center">Battery Indicator</TableCell>
                            <TableCell align="center">User Connected</TableCell>
                            <TableCell align="center">Current Location</TableCell>
                            <TableCell align="center">Destination Location</TableCell>
                            <TableCell align="center">Vehicle Number</TableCell>
                            <TableCell align="center">Trip Date</TableCell>
                            <TableCell align="center">Arrival Date</TableCell>
                            <TableCell align="center">Arrival Time</TableCell>
                            <TableCell align="center">State</TableCell>
                            <TableCell align="center">Partner Type</TableCell>
                            <TableCell align="center">Partner Name</TableCell>
                            <TableCell align="center"> Partner Contact</TableCell>
                            <TableCell align="center">Movement Indicator</TableCell>
                            <TableCell align="center">Tracking</TableCell>
                            <TableCell align="center">UserFBK</TableCell>
                            <TableCell align="center">Wellbeing Response</TableCell>
                            <TableCell align="center">Last Known Location</TableCell>
                            <TableCell align="center">File</TableCell>
                            <TableCell align="center">Report Generate</TableCell>

                            <TableCell align="center">Action</TableCell>
                            {allData.map((user, index) => (
                              <TableRow key={index} sx={{ height: "auto" }}>
                                <TableCell sx={{ padding: "6px" }} component="th" scope="row">
                                  <Avatar
                                    src={user?.userData?.userImage}
                                    alt={user?.userData?.userName}
                                    style={{ marginRight: "10px" }}
                                  />
                                  {user?.userData?.userName}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 80, whiteSpace: "normal" }}
                                >
                                  <BatteryStatusChip
                                    status={getBatteryPercent(
                                      user?.userData?.BatteryIndicator ??
                                      user?.BatteryIndicator ??
                                      user?.batteryLevel
                                    ).toFixed(0)}
                                    isCharging={Boolean(
                                      user?.userData?.isCharging ??
                                      user?.isCharging ??
                                      user?.payload?.isCharging
                                    )}
                                  />
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 80, whiteSpace: "normal" }}
                                >
                                  <WellBeingConnect user={user?.userData} />
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 300, whiteSpace: "normal" }}
                                >
                                  {user?.currentLocTxt}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 150, whiteSpace: "normal" }}
                                >
                                  {/* {user.destLocTxt?.length ? user.destLocTxt.join("\n") : "---"} */}
{/* {
  Array.isArray(user?.destLocTxt)
    ? user.destLocTxt.join("\n")
    : user?.destLocTxt || "---"
} */}
<TableCell
  align="center"
  className="rounded"
  sx={{ minWidth: 150, whiteSpace: "normal" }}
>
  {Array.isArray(user?.destLocTxt)
    ? user.destLocTxt.join(", ")
    : typeof user?.destLocTxt === "string"
    ? user.destLocTxt
    : "---"}
</TableCell>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 50, whiteSpace: "normal" }}
                                >
                                  {user.VehicleNumber || "N/A"}
                                </TableCell>

                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 100, whiteSpace: "normal" }}
                                >
                                  {user?.TripDate || "N/A"}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 100, whiteSpace: "normal" }}
                                >
                                  {user?.ArrivalDate || "N/A"}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 100, whiteSpace: "normal" }}
                                >
                                  {user?.ArrivalTime || "N/A"}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 80, whiteSpace: "normal" }}
                                >
                                  {user?.userData?.state || "N/A"}
                                </TableCell>

                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 80, whiteSpace: "normal" }}
                                >
                                  {user?.PartenerType || "N/A"}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 80, whiteSpace: "normal" }}
                                >
                                  {user?.Name || "N/A"}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 80, whiteSpace: "normal" }}
                                >
                                  {user?.PhoneNumber || "N/A"}
                                </TableCell>

                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 50, whiteSpace: "normal" }}
                                >
                                  <WellBeingMovement user={user?.userData} />
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 50, whiteSpace: "normal" }}
                                >
                                  <Switch
                                    checked={user?.userData?.tracking}
                                    onChange={() => handleToggle(user, index)}
                                    color="primary"
                                    borderRadius="5px"
                                  />
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 150, whiteSpace: "normal" }}
                                >
                                  <UserFBK
                                    status={user?.userData?.userFBK}
                                    userName={user?.userData?.userName}
                                  />
                                </TableCell>
                                <TableCell align="center" className="rounded" sx={{ padding: "6px" }}>
                                  {user?.Wellbeing_Response ||
                                    user?.wellbeingResponse ||
                                    user?.userData?.Wellbeing_Response ||
                                    user?.userData?.wellbeingResponse ||
                                    user?.payload?.Wellbeing_Response ||
                                    user?.payload?.wellbeingResponse ||
                                    "N/A"}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 100, whiteSpace: "normal" }}
                                >
                                  <Link to={`/locate/${user?.userId}?userData=truck`}>
                                    <button className="btn btn-danger btn-sm">Track</button>
                                  </Link>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 100, whiteSpace: "normal" }}
                                >
                                  <File props={user} />
                                  {/* <Chat /> */}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 100, whiteSpace: "normal" }}
                                >
                                  <ReportGenerate props={user?.userData} />
                                </TableCell>

                                <TableCell
                                  align="center"
                                  className="rounded"
                                  sx={{ minWidth: 100, whiteSpace: "normal" }}
                                >
                                  <LongMenu
                                    props={user}
                                    VitualWellBeingHomeCheck={false}
                                    virtualTravelGuard={true}
                                    virtualId={user?.wellbeingIdd}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </TableHead>
                      </Table>
                    </TableContainer>
                  </MDBox>
                ) : (
                  <EmptyData />
                )}
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};
export default React.memo(VirtualTravelGuard);
